import { useState, useEffect, useCallback, useMemo } from "react";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { CUENTAS_ER } from "../../constants/clasificacionContable";
import { useAuthContext } from "../../context/auth";
import {
  getProveedores,
  crearProveedor,
  actualizarProveedor,
  getCostosAsociados,
  guardarCostosAsociados,
} from "../../utils/proveedores";
import { client } from "../../utils/supabase";
import { useCurrency } from "../../utils/useCurrency";
import { Header } from "../Header";
import KebabMenu from "../KebabMenu";
import styles from "./GestionProveedores.module.scss";

const formatRut = (rut) => {
  if (!rut) return "—";
  const clean = rut.replace(/\./g, "").trim();
  const parts = clean.split("-");
  if (parts.length !== 2) return rut;
  const num = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${num}-${parts[1]}`;
};
const MONEDAS = ["CLP", "USD", "PEN", "UF"];

// Sanitiza lo tipeado en un input de monto decimal: deja dígitos y UN solo separador (coma o
// punto), preservando la coma mientras el usuario escribe. Al persistir se normaliza a punto.
const sanitizarMontoTipeo = (raw) => {
  const s = String(raw).replace(/[^\d,.]/g, "");
  const idx = s.search(/[,.]/);
  if (idx === -1) return s;
  return s.slice(0, idx) + s[idx] + s.slice(idx + 1).replace(/[,.]/g, "");
};

const GestionProveedores = () => {
  const { pais, id: userId } = useAuthContext();
  const { formatAmount } = useCurrency();
  const navigate = useNavigate();
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [filtroRecurrente, setFiltroRecurrente] = useState(false);
  const [filtroExtranjero, setFiltroExtranjero] = useState(false);
  const [filtroMoneda, setFiltroMoneda] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({});
  const [showDetalle, setShowDetalle] = useState(false);
  const [detalleProveedor, setDetalleProveedor] = useState(null);

  const cargar = useCallback(async () => {
    if (!pais || pais === "Region") return;
    setLoading(true);
    try {
      const data = await getProveedores(pais, false);
      setProveedores(data);
    } catch {
      toast.error("Error cargando proveedores");
    } finally {
      setLoading(false);
    }
  }, [pais]);

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cargar]);

  const proveedoresFiltrados = useMemo(
    () =>
      proveedores.filter((p) => {
        if (!p.habilitado) return false;
        if (
          busqueda &&
          !p.nombre?.toLowerCase().includes(busqueda.toLowerCase()) &&
          !p.alias?.toLowerCase().includes(busqueda.toLowerCase())
        )
          return false;
        if (filtroRecurrente && !p.es_recurrente) return false;
        if (filtroExtranjero && !p.es_extranjero) return false;
        if (filtroMoneda && p.moneda_default !== filtroMoneda) return false;
        return true;
      }),
    [proveedores, busqueda, filtroRecurrente, filtroExtranjero, filtroMoneda]
  );

  const handleNuevo = () => {
    setEditando(null);
    setForm({
      nombre: "",
      alias: "",
      concepto_flujo: "",
      rut_ruc: "",
      pais,
      es_extranjero: false,
      es_recurrente: false,
      es_factoring: false,
      habilitado: true,
      contacto_nombre: "",
      contacto_email: "",
      cuenta_er_default: "",
      tipo_contable_default: "",
      moneda_default: "",
      monto_recurrente: "",
      moneda_recurrente: "",
      dia_pago: 30,
      recurrente_desde: "",
      recurrente_hasta: "",
      recurrente_con_contrato: false,
      cuotas_adelantadas: 0,
      costos_asociados: [],
    });
    setShowModal(true);
  };

  const handleEditar = async (p) => {
    setEditando(p.id);
    // Los costos asociados viven en otra tabla → se cargan aparte y se cablean al form.
    let costos = [];
    try {
      costos = await getCostosAsociados(p.id);
    } catch {
      costos = [];
    }
    setForm({ ...p, costos_asociados: costos });
    setShowModal(true);
  };

  const handleGuardar = async () => {
    if (!form.nombre) return toast.error("El nombre es obligatorio");
    try {
      const payload = { ...form };
      delete payload.alias_banco;
      // costos_asociados es un campo SOLO del form (vive en otra tabla) → fuera del payload proveedor.
      // El monto se normaliza coma→punto para que Number(...) en guardarCostosAsociados no dé NaN.
      const costos = (form.costos_asociados || []).map((c) => ({
        ...c,
        monto: c.monto === "" || c.monto == null ? c.monto : String(c.monto).replace(",", "."),
      }));
      delete payload.costos_asociados;
      if (payload.monto_recurrente === "") payload.monto_recurrente = null;
      if (payload.moneda_default === "") payload.moneda_default = null;
      // Alias vacío/espacios → NULL (el label de la proyección cae a la razón social: prov.alias || prov.nombre).
      payload.alias = payload.alias?.trim() || null;
      // Concepto en el flujo vacío/espacios → NULL (el label muestra solo el alias).
      payload.concepto_flujo = payload.concepto_flujo?.trim() || null;

      // Vigencia de la recurrencia (recurrente_desde/hasta): INDEPENDIENTE del contrato. Es el
      // mecanismo para dar de baja un recurrente (poner recurrente_hasta); el motor
      // (proveedoresRecurrentes.js / conciliacionCostoRecurrente.js) corta por esa ventana sin
      // mirar recurrente_con_contrato. Solo se limpia si el proveedor deja de ser recurrente.
      // "Con contrato" ya solo gobierna cuotas adelantadas + costos asociados (no el plazo).
      const esRecurrente = !!payload.es_recurrente;
      const conContrato = !!(esRecurrente && payload.recurrente_con_contrato);
      if (!esRecurrente) {
        // Destildar "Proyectar como recurrente" limpia todo el bloque recurrente.
        payload.recurrente_desde = null;
        payload.recurrente_hasta = null;
        payload.recurrente_con_contrato = false;
        payload.cuotas_adelantadas = 0;
      } else {
        // La vigencia persiste siempre que sea recurrente (con o sin contrato).
        if (payload.recurrente_desde === "") payload.recurrente_desde = null;
        if (payload.recurrente_hasta === "") payload.recurrente_hasta = null;
        if (!conContrato) {
          payload.recurrente_con_contrato = false;
          payload.cuotas_adelantadas = 0;
        } else {
          payload.cuotas_adelantadas =
            payload.cuotas_adelantadas === "" || payload.cuotas_adelantadas == null
              ? 0
              : Number(payload.cuotas_adelantadas);
        }
      }

      let proveedorId = editando;
      if (editando) {
        await actualizarProveedor(editando, payload);
        toast.success("Proveedor actualizado");
      } else {
        const res = await crearProveedor({ ...payload, pais });
        if (res?.yaExiste) {
          toast.error(
            `Ya existe un proveedor con ese RUT: ${res.proveedor.nombre} (ID ${res.proveedor.id}). No se creó el duplicado.`
          );
          return;
        }
        proveedorId = res?.id;
        toast.success("Proveedor creado");
      }

      // Persistir los costos asociados (replace). Sin contrato → se limpian (lista vacía).
      if (proveedorId) await guardarCostosAsociados(proveedorId, conContrato ? costos : []);

      setShowModal(false);
      await cargar();
    } catch {
      toast.error("Error al guardar");
    }
  };

  const handleEliminar = async (proveedor) => {
    try {
      const { count: countFacturas } = await client
        .from("facturas_sii_recibidas")
        .select("id", { count: "exact", head: true })
        .eq("proveedor_id", proveedor.id);

      const { count: countExternas } = await client
        .from("facturas_externas")
        .select("id", { count: "exact", head: true })
        .eq("proveedor_id", proveedor.id);

      // Bloque C: contar también boletas de honorarios — un proveedor con solo boletas no debe
      // poder borrarse sin el candado (dejaría boletas huérfanas).
      const { count: countBoletas } = await client
        .from("boletas_honorarios")
        .select("id", { count: "exact", head: true })
        .eq("proveedor_id", proveedor.id);

      const tieneVinculos = (countFacturas || 0) + (countExternas || 0) + (countBoletas || 0) > 0;

      if (tieneVinculos && userId !== 1 && userId !== 4) {
        toast.error("Este proveedor tiene facturas/boletas asociadas. Solo Ignacio puede eliminarlo.");
        return;
      }

      const confirmMsg = tieneVinculos
        ? `"${proveedor.alias || proveedor.nombre}" tiene facturas/boletas asociadas. ¿Confirmar eliminación?`
        : `¿Eliminar "${proveedor.alias || proveedor.nombre}"?`;

      if (!window.confirm(confirmMsg)) return;

      if (tieneVinculos && !window.confirm("Segunda confirmación requerida. ¿Estás seguro?")) return;

      await client.from("proveedores").update({ habilitado: false }).eq("id", proveedor.id);
      toast.success("Proveedor deshabilitado");
      await cargar();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const handleVerDetalle = (p) => {
    setDetalleProveedor(p);
    setShowDetalle(true);
  };

  const set = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  // Helpers de la mini-lista de costos asociados (form.costos_asociados). Se persisten en
  // proveedor_costos_asociados al guardar. La moneda default de un costo nuevo hereda la del
  // proveedor (o CLP).
  const addCosto = () =>
    setForm((p) => ({
      ...p,
      costos_asociados: [
        ...(p.costos_asociados || []),
        { concepto: "", monto: "", moneda: p.moneda_default || "CLP", tipo: "mensual", dia_pago: 30, fecha: "" },
      ],
    }));
  const updateCosto = (idx, field, value) =>
    setForm((p) => ({
      ...p,
      costos_asociados: (p.costos_asociados || []).map((c, i) => (i === idx ? { ...c, [field]: value } : c)),
    }));
  const removeCosto = (idx) =>
    setForm((p) => ({ ...p, costos_asociados: (p.costos_asociados || []).filter((_, i) => i !== idx) }));

  return (
    <div className={styles.wrapper}>
      <Header title="Proveedores">
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Buscar razón social o alias..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            style={{ width: 240 }}
          />
          <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.82rem", cursor: "pointer" }}>
            <input type="checkbox" checked={filtroRecurrente} onChange={(e) => setFiltroRecurrente(e.target.checked)} />
            Recurrente
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.82rem", cursor: "pointer" }}>
            <input type="checkbox" checked={filtroExtranjero} onChange={(e) => setFiltroExtranjero(e.target.checked)} />
            Extranjero
          </label>
          <select
            className="form-select form-select-sm"
            value={filtroMoneda}
            onChange={(e) => setFiltroMoneda(e.target.value)}
            style={{ width: 100 }}
          >
            <option value="">Moneda</option>
            <option value="CLP">CLP</option>
            <option value="USD">USD</option>
            <option value="PEN">PEN</option>
          </select>
          <button className="btn btn-sm btn-primary" onClick={handleNuevo}>
            + Proveedor
          </button>
        </div>
      </Header>

      <div className={styles.content}>
        {loading ? (
          <div className={styles.empty}>Cargando...</div>
        ) : (
          <table className="table table-bordered table-striped table-hover table-sm">
            <thead className="table-dark">
              <tr>
                <th style={{ width: "32%" }}>Razón social</th>
                <th className="text-center" style={{ width: "11%" }}>
                  {pais === "Chile" ? "RUT" : "RUC"}
                </th>
                <th className="text-center" style={{ width: "8%" }}>
                  Moneda
                </th>
                <th className="text-end" style={{ width: "10%" }}>
                  Monto semilla
                </th>
                <th style={{ width: "17%" }}>Cuenta ER</th>
                <th className="text-center" style={{ width: "12%" }}>
                  Tipo
                </th>
                <th className="text-center" style={{ width: "6%", whiteSpace: "nowrap" }}>
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody>
              {proveedoresFiltrados.length === 0 && (
                <tr>
                  <td colSpan={7} className={styles.empty}>
                    Sin proveedores
                  </td>
                </tr>
              )}
              {proveedoresFiltrados.map((p) => {
                const badges = [];
                if (p.es_recurrente) badges.push(["r", styles.badgeBlue, "Recurrente"]);
                if (p.es_extranjero) badges.push(["e", styles.badgeGreen, "Extranjero"]);
                if (p.es_factoring) badges.push(["f", styles.badgeGray, "Factoring"]);
                if (!p.habilitado) badges.push(["i", styles.badgeRed, "Inactivo"]);
                return (
                  <tr key={p.id} onClick={() => handleVerDetalle(p)} style={{ opacity: p.habilitado ? 1 : 0.4 }}>
                    <td className="text-start text-capitalize">{(p.nombre || "").toLowerCase()}</td>
                    <td className="text-center">{formatRut(p.rut_ruc)}</td>
                    <td className="text-center">{p.moneda_default || "—"}</td>
                    <td className="text-end">
                      {p.monto_recurrente
                        ? formatAmount(p.monto_recurrente, p.moneda_default || p.moneda_recurrente)
                        : "—"}
                    </td>
                    <td style={{ fontSize: "0.78rem" }}>
                      {p.cuenta_er_default ? (
                        CUENTAS_ER.find((c) => c.value === p.cuenta_er_default)?.label || p.cuenta_er_default
                      ) : (
                        <span style={{ color: "#ccc" }}>Sin asignar</span>
                      )}
                    </td>
                    <td className="text-center" style={{ whiteSpace: "nowrap" }}>
                      {badges.length === 0 ? (
                        "—"
                      ) : (
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", justifyContent: "center" }}>
                          {badges.map(([k, cls, label]) => (
                            <span key={k} className={cls}>
                              {label}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="text-center" style={{ whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}>
                      <KebabMenu
                        items={[
                          {
                            label: "Ver estado de cuenta",
                            onClick: () => navigate(`/admin/estado-cuenta?tipo=proveedor&id=${p.id}`),
                          },
                          { label: "Editar", onClick: () => handleEditar(p) },
                          { label: "Eliminar", danger: true, onClick: () => handleEliminar(p) },
                        ]}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem" }}>{editando ? "Editar proveedor" : "Nuevo proveedor"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label className={styles.label}>Razón social *</label>
              <input
                className="form-control form-control-sm"
                value={form.nombre || ""}
                onChange={(e) => set("nombre", e.target.value)}
                placeholder="Nombre formal completo"
              />
            </div>

            <div>
              <label className={styles.label}>Alias (nombre en el flujo)</label>
              <input
                className="form-control form-control-sm"
                value={form.alias || ""}
                onChange={(e) => set("alias", e.target.value)}
                placeholder="Corto y legible (ej. Oficina)"
              />
            </div>

            <div>
              <label className={styles.label}>Concepto en el flujo (opcional)</label>
              <input
                className="form-control form-control-sm"
                value={form.concepto_flujo || ""}
                onChange={(e) => set("concepto_flujo", e.target.value)}
                placeholder="Sufijo del label (ej. Alquiler) → “Oficina — Alquiler”"
              />
            </div>

            <div>
              <label className={styles.label}>{pais === "Chile" ? "RUT" : "RUC"}</label>
              <input
                className="form-control form-control-sm"
                value={form.rut_ruc || ""}
                onChange={(e) => set("rut_ruc", e.target.value)}
              />
            </div>

            <div>
              <label className={styles.label}>Contacto</label>
              <input
                className="form-control form-control-sm"
                value={form.contacto_nombre || ""}
                onChange={(e) => set("contacto_nombre", e.target.value)}
                placeholder="Nombre"
              />
            </div>

            <div>
              <label className={styles.label}>Email</label>
              <input
                className="form-control form-control-sm"
                type="email"
                value={form.contacto_email || ""}
                onChange={(e) => set("contacto_email", e.target.value)}
              />
            </div>

            <div>
              <label className={styles.label}>Cuenta ER default</label>
              <select
                className="form-select form-select-sm"
                value={form.cuenta_er_default || ""}
                onChange={(e) => set("cuenta_er_default", e.target.value)}
              >
                <option value="">Sin asignar</option>
                {CUENTAS_ER.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={styles.label}>Moneda default</label>
              <select
                className="form-select form-select-sm"
                value={form.moneda_default || ""}
                onChange={(e) => set("moneda_default", e.target.value)}
              >
                <option value="">Sin asignar</option>
                {MONEDAS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ gridColumn: "1 / -1", display: "flex", gap: 20, flexWrap: "wrap" }}>
              {[
                { field: "es_extranjero", label: "Proveedor extranjero" },
                { field: "es_recurrente", label: "Proyectar como recurrente" },
                { field: "es_factoring", label: "Empresa de factoring" },
                { field: "habilitado", label: "Habilitado" },
              ].map(({ field, label }) => (
                <label
                  key={field}
                  style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.82rem", cursor: "pointer" }}
                >
                  <input type="checkbox" checked={!!form[field]} onChange={(e) => set(field, e.target.checked)} />
                  {label}
                </label>
              ))}
            </div>

            {form.es_recurrente && (
              <>
                <div>
                  <label className={styles.label}>Monto semilla (opcional)</label>
                  <input
                    className="form-control form-control-sm"
                    type="number"
                    value={form.monto_recurrente || ""}
                    onChange={(e) => set("monto_recurrente", e.target.value)}
                  />
                  <small style={{ fontSize: "0.72rem", color: "#888", display: "block", marginTop: 2 }}>
                    Solo se usa si el proveedor no tiene facturas en los últimos meses. Con historial, la proyección usa
                    el promedio real de sus facturas.
                  </small>
                </div>
                <div>
                  <label className={styles.label}>Día de pago</label>
                  <input
                    className="form-control form-control-sm"
                    type="number"
                    min={1}
                    max={31}
                    value={form.dia_pago || 30}
                    onChange={(e) => set("dia_pago", parseInt(e.target.value))}
                  />
                </div>
                {/* Vigencia de la recurrencia: INDEPENDIENTE del contrato. Es el mecanismo para dar de
                    baja un recurrente (poner "Hasta"); el motor corta por esta ventana sin mirar
                    recurrente_con_contrato. Vive arriba del checkbox de contrato a propósito. */}
                <div style={{ gridColumn: "1 / -1", marginTop: 4 }}>
                  <div style={{ fontSize: "0.82rem", fontWeight: 600, color: "#495057" }}>
                    Vigencia de la recurrencia
                  </div>
                </div>
                <div>
                  <label className={styles.label}>Desde (opcional)</label>
                  <input
                    className="form-control form-control-sm"
                    type="date"
                    value={form.recurrente_desde || ""}
                    onChange={(e) => set("recurrente_desde", e.target.value)}
                  />
                  <small style={{ fontSize: "0.72rem", color: "#888", display: "block", marginTop: 2 }}>
                    No se proyecta antes de este mes. Vacío: sin límite.
                  </small>
                </div>
                <div>
                  <label className={styles.label}>Hasta (opcional)</label>
                  <input
                    className="form-control form-control-sm"
                    type="date"
                    value={form.recurrente_hasta || ""}
                    onChange={(e) => set("recurrente_hasta", e.target.value)}
                  />
                  <small style={{ fontSize: "0.72rem", color: "#888", display: "block", marginTop: 2 }}>
                    Último mes que se proyecta, inclusive. Vacío: sin fin. Ej: 31/08/2026 proyecta hasta agosto y nada
                    desde septiembre.
                  </small>
                </div>

                <div style={{ gridColumn: "1 / -1" }}>
                  <label
                    style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.82rem", cursor: "pointer" }}
                  >
                    <input
                      type="checkbox"
                      checked={!!form.recurrente_con_contrato}
                      onChange={(e) => set("recurrente_con_contrato", e.target.checked)}
                    />
                    Recurrente con contrato (costos asociados)
                  </label>
                  <small style={{ fontSize: "0.72rem", color: "#888", display: "block", marginTop: 2 }}>
                    Sin tildar = suscripción abierta (comportamiento actual). Con contrato: se definen cuotas
                    adelantadas y costos que lo acompañan. El plazo (vigencia) se define arriba, no depende de esto.
                  </small>
                </div>

                {form.recurrente_con_contrato && (
                  <>
                    <div>
                      <label className={styles.label}>Cuotas ya pagadas por adelantado</label>
                      <input
                        className="form-control form-control-sm"
                        type="number"
                        min={0}
                        value={form.cuotas_adelantadas ?? 0}
                        onChange={(e) =>
                          set("cuotas_adelantadas", e.target.value === "" ? 0 : parseInt(e.target.value))
                        }
                      />
                      <small style={{ fontSize: "0.72rem", color: "#888", display: "block", marginTop: 2 }}>
                        De la línea principal (ej. arriendo pagado al firmar = 1). No se proyecta aún (Fase 2).
                      </small>
                    </div>

                    <div style={{ gridColumn: "1 / -1" }}>
                      <label className={styles.label} style={{ display: "flex", justifyContent: "space-between" }}>
                        <span>Costos asociados</span>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          style={{ fontSize: "0.72rem", padding: "1px 8px" }}
                          onClick={addCosto}
                        >
                          + Agregar costo
                        </button>
                      </label>
                      {(form.costos_asociados || []).length === 0 ? (
                        <div style={{ fontSize: "0.74rem", color: "#999", padding: "4px 0" }}>
                          Sin costos asociados. Ej: gastos comunes.
                        </div>
                      ) : (
                        (form.costos_asociados || []).map((c, idx) => (
                          <div
                            key={idx}
                            style={{ display: "flex", gap: 6, marginBottom: 6, alignItems: "center", flexWrap: "wrap" }}
                          >
                            <input
                              className="form-control form-control-sm"
                              style={{ flex: "2 1 140px" }}
                              placeholder="Concepto (ej. Gastos comunes)"
                              value={c.concepto || ""}
                              onChange={(e) => updateCosto(idx, "concepto", e.target.value)}
                            />
                            <input
                              className="form-control form-control-sm"
                              style={{ flex: "1 1 90px" }}
                              type="text"
                              inputMode="decimal"
                              placeholder="Monto"
                              value={c.monto ?? ""}
                              onChange={(e) => updateCosto(idx, "monto", sanitizarMontoTipeo(e.target.value))}
                            />
                            <select
                              className="form-select form-select-sm"
                              style={{ flex: "0 1 80px" }}
                              value={c.moneda || "CLP"}
                              onChange={(e) => updateCosto(idx, "moneda", e.target.value)}
                            >
                              {MONEDAS.map((m) => (
                                <option key={m} value={m}>
                                  {m}
                                </option>
                              ))}
                            </select>
                            <select
                              className="form-select form-select-sm"
                              style={{ flex: "0 1 95px" }}
                              value={c.tipo || "mensual"}
                              onChange={(e) => updateCosto(idx, "tipo", e.target.value)}
                              title="Mensual (recurrente) o Único (un solo pago con fecha)"
                            >
                              <option value="mensual">Mensual</option>
                              <option value="unico">Único</option>
                            </select>
                            {(c.tipo || "mensual") === "unico" ? (
                              <input
                                className="form-control form-control-sm"
                                style={{ flex: "0 1 140px" }}
                                type="date"
                                title="Fecha del pago único"
                                value={c.fecha || ""}
                                onChange={(e) => updateCosto(idx, "fecha", e.target.value)}
                              />
                            ) : (
                              <input
                                className="form-control form-control-sm"
                                style={{ flex: "0 1 70px" }}
                                type="number"
                                min={1}
                                max={31}
                                placeholder="Día"
                                value={c.dia_pago ?? ""}
                                onChange={(e) => updateCosto(idx, "dia_pago", e.target.value)}
                              />
                            )}
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              style={{ fontSize: "0.72rem", padding: "1px 8px" }}
                              onClick={() => removeCosto(idx)}
                            >
                              ✕
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" size="sm" onClick={handleGuardar}>
            Guardar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDetalle} onHide={() => setShowDetalle(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem" }}>Detalle — {detalleProveedor?.nombre}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detalleProveedor && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, fontSize: "0.85rem" }}>
              <div>
                <strong>Razón social:</strong> {detalleProveedor.nombre || "—"}
              </div>
              <div>
                <strong>{pais === "Chile" ? "RUT" : "RUC"}:</strong> {formatRut(detalleProveedor.rut_ruc)}
              </div>
              <div>
                <strong>Contacto:</strong> {detalleProveedor.contacto_nombre || "—"}
              </div>
              <div>
                <strong>Email:</strong> {detalleProveedor.contacto_email || "—"}
              </div>
              <div>
                <strong>Cuenta ER:</strong>{" "}
                {detalleProveedor.cuenta_er_default
                  ? CUENTAS_ER.find((c) => c.value === detalleProveedor.cuenta_er_default)?.label ||
                    detalleProveedor.cuenta_er_default
                  : "—"}
              </div>
              <div>
                <strong>Habilitado:</strong> {detalleProveedor.habilitado ? "Sí" : "No"}
              </div>
              <div>
                <strong>Extranjero:</strong> {detalleProveedor.es_extranjero ? "Sí" : "No"}
              </div>
              <div>
                <strong>Factoring:</strong> {detalleProveedor.es_factoring ? "Sí" : "No"}
              </div>
              <div>
                <strong>Recurrente:</strong> {detalleProveedor.es_recurrente ? "Sí" : "No"}
              </div>
              {detalleProveedor.es_recurrente && (
                <>
                  <div>
                    <strong>Día de pago:</strong> {detalleProveedor.dia_pago || "—"}
                  </div>
                  <div>
                    <strong>Monto semilla:</strong>{" "}
                    {formatAmount(
                      detalleProveedor.monto_recurrente,
                      detalleProveedor.moneda_default || detalleProveedor.moneda_recurrente
                    )}
                  </div>
                  <div>
                    <strong>Moneda:</strong>{" "}
                    {detalleProveedor.moneda_default || detalleProveedor.moneda_recurrente || "—"}
                  </div>
                  {detalleProveedor.recurrente_desde && (
                    <div>
                      <strong>Vigencia desde:</strong> {detalleProveedor.recurrente_desde}
                    </div>
                  )}
                  {detalleProveedor.recurrente_hasta && (
                    <div>
                      <strong>Vigencia hasta:</strong> {detalleProveedor.recurrente_hasta}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowDetalle(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default GestionProveedores;
